import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { useUnifiedPolling } from '../hooks/useUnifiedPolling';

interface PollingContextType {
  addVideoStatusPolling: (videoId: string, callback: (data: any) => void, priority?: 'high' | 'normal' | 'low') => void;
  removeVideoStatusPolling: (videoId: string) => void;
  addDashboardPolling: (callback: (data: any) => void) => void;
  removeDashboardPolling: () => void;
  addStatsPolling: (callback: (data: any) => void) => void;
  removeStatsPolling: () => void;
  updateVideoPriority: (videoId: string, priority: 'high' | 'normal' | 'low') => void;
  isPolling: boolean;
  totalTargets: number;
}

const PollingContext = createContext<PollingContextType | undefined>(undefined);

interface PollingProviderProps {
  children: ReactNode;
}

export const PollingProvider: React.FC<PollingProviderProps> = ({ children }) => {
  const { 
    addTarget, 
    removeTarget, 
    updateTargetPriority, 
    isPolling, 
    totalTargets 
  } = useUnifiedPolling();

  const addVideoStatusPolling = useCallback((
    videoId: string, 
    callback: (data: any) => void, 
    priority: 'high' | 'normal' | 'low' = 'normal'
  ) => {
    addTarget({
      id: `video_status_${videoId}`,
      type: 'video_status',
      priority,
      callback
    });
  }, [addTarget]);

  const removeVideoStatusPolling = useCallback((videoId: string) => {
    removeTarget(`video_status_${videoId}`);
  }, [removeTarget]);

  const addDashboardPolling = useCallback((callback: (data: any) => void) => {
    addTarget({
      id: 'dashboard',
      type: 'dashboard',
      priority: 'normal',
      callback
    });
  }, [addTarget]);

  const removeDashboardPolling = useCallback(() => {
    removeTarget('dashboard');
  }, [removeTarget]);

  const addStatsPolling = useCallback((callback: (data: any) => void) => {
    addTarget({
      id: 'user_stats',
      type: 'user_stats',
      priority: 'low',
      callback
    });
  }, [addTarget]);

  const removeStatsPolling = useCallback(() => {
    removeTarget('user_stats');
  }, [removeTarget]);

  const updateVideoPriority = useCallback((videoId: string, priority: 'high' | 'normal' | 'low') => {
    updateTargetPriority(`video_status_${videoId}`, priority);
  }, [updateTargetPriority]);

  const contextValue: PollingContextType = {
    addVideoStatusPolling,
    removeVideoStatusPolling,
    addDashboardPolling,
    removeDashboardPolling,
    addStatsPolling,
    removeStatsPolling,
    updateVideoPriority,
    isPolling,
    totalTargets
  };

  return (
    <PollingContext.Provider value={contextValue}>
      {children}
    </PollingContext.Provider>
  );
};

export const usePolling = (): PollingContextType => {
  const context = useContext(PollingContext);
  if (!context) {
    throw new Error('usePolling must be used within a PollingProvider');
  }
  return context;
}; 